import { Box, Button, CloseButton, Stack, Text } from "@mantine/core";
import { visualsFor } from "./categories";
import { formatSentAt } from "./time";
import type { PortalAnnouncement } from "./types";

/** Edge-to-edge **takeover** overlay — used for ``display_type =
 *  fullscreen_takeover`` (evacuations / critical alerts that cover the whole
 *  viewport and suppress everything else). The regular ``fullscreen`` type
 *  renders as a centered, page-embedded modal instead — see
 *  ``AnnouncementModal``.
 *
 *  Renders the category palette (or the announcement image as a backdrop)
 *  with the title/message centered. ``onClose`` shows a Dismiss affordance;
 *  the caller decides whether closing is permanent or session-only. */
export function AnnouncementFullscreen({
  announcement,
  onClose,
}: {
  announcement: PortalAnnouncement;
  onClose?: () => void;
}) {
  const visuals = visualsFor(announcement.category);
  const message = announcement.message?.trim();
  const image = announcement.image;

  return (
    <Box
      pos="fixed"
      c="white"
      p={32}
      style={{
        inset: 0,
        zIndex: 1000,
        background: visuals.background,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...(visuals.pulseLed ? { animation: "nodus-ann-pulse 1.6s ease-in-out infinite" } : {}),
      }}
    >
      {image?.url && (
        <Box
          pos="absolute"
          style={{
            inset: 0,
            backgroundImage: `url(${image.url})`,
            backgroundSize: image.object_fit === "contain" ? "contain" : "cover",
            backgroundPosition: image.object_position || "center",
            backgroundRepeat: "no-repeat",
            opacity: 0.35,
          }}
        />
      )}
      {onClose && (
        <CloseButton
          aria-label="Dismiss announcement"
          onClick={onClose}
          c="white"
          variant="transparent"
          size="lg"
          pos="absolute"
          style={{ right: 16, top: 16 }}
        />
      )}
      <Stack align="center" gap="md" maw={680} style={{ position: "relative" }}>
        <Text fz={48}>{visuals.icon}</Text>
        <Text fz="xs" fw={700} style={{ color: visuals.accent, textTransform: "uppercase", letterSpacing: 1 }}>
          {visuals.label}
          {announcement.is_site_wide ? " · SITE-WIDE" : ""}
        </Text>
        <Text fz={28} fw={700} ta="center" c="white">
          {announcement.title?.trim() || visuals.label}
        </Text>
        {message && (
          <Text fz="lg" ta="center" c="white">
            {message}
          </Text>
        )}
        {formatSentAt(announcement.raised_at) && (
          <Text fz="sm" ta="center" c="white" style={{ opacity: 0.8 }}>
            Sent {formatSentAt(announcement.raised_at)}
          </Text>
        )}
        {onClose && (
          <Button variant="white" color="dark" onClick={onClose} mt="md">
            Dismiss
          </Button>
        )}
      </Stack>
      <style>{`@keyframes nodus-ann-pulse{0%,100%{opacity:1}50%{opacity:.92}}`}</style>
    </Box>
  );
}
